import { analyticsPageName } from './helpers';

export const initGlobalEventListeners = async () => {
  // USE THIS METHOD TO SETUP PAGES THAT AREN'T BUILT WITH LIT ELEMENT

  // Global Search Page Events
  const globalSearchWrapper = document.querySelector('#global-search-results');
  if (globalSearchWrapper) setupGlobalSearchTracking(globalSearchWrapper);

  // CEUS Page
  const ceusPageHeader = document.querySelector('#ceus-page-header');
  if (ceusPageHeader) setupCEUsPageTracking(ceusPageHeader);

  // Bitreel Digital Print
  const bitreelDigitalPrintWrapper = document.querySelector(
    '#bitreel-digital-print'
  );
  if (bitreelDigitalPrintWrapper)
    setupBitreelDigitalPrintPageTracking(bitreelDigitalPrintWrapper);
};

export const adobeAnalyticsPageLoad = async detail => {
  // parse pagename values and set them on the window.analytics object
  const pageNameString = await analyticsPageName();
  if (pageNameString) {
    pageNameString.split('|').forEach((value, i) => {
      window.analytics.site[`sitesection${i === 0 ? '' : i + 1}`] = value;
    });
  }

  detail = !!detail ? detail : {};

  // trigger page load event
  const eventData = {
    type: 'load',
    detail,
  };

  // Adobe Analytics global search handling
  const globalSearchWrapper = document.querySelector('#global-search-results');
  if (globalSearchWrapper) {
    // let Adobe know a search happened on page load
    const wrapperData = JSON.parse(globalSearchWrapper.dataset.results);
    const resultsFound = !!wrapperData.results_found;
    const term = adobeCleanString(wrapperData.search_term);
    eventData.detail['search'] = {
      term,
      results: resultsFound,
    };
  }

  fireAdobeAnalyticsEvent(eventData);
  initGlobalEventListeners();
};

export const updateAnalyticsObectWithCartData = cart => {
  if (cart && cart.data) {
    // set cart id
    const id = cart.data.id;
    if (id) window.analytics.cart.id = id;
  }
};

export const fireAdobeAnalyticsEvent = async (
  eventData = { detail: {} },
  target
) => {
  const { detail } = eventData,
    eventName = `analytics:${eventData.type}`;

  // Make the event using the passed in event data
  const event = new CustomEvent(eventName, {
    detail,
  });

  if (typeof event.detail === 'object') {
    const leaveTheseDirty = ['eVar108', 'eVar109', 'eVar230'];
    const useHyphenDelimiter = ['eVar0', 'eVar35'];

    for (const [key, value] of Object.entries(event.detail)) {
      if (typeof value !== 'string' || stringIsUrl(value)) continue;
      if (leaveTheseDirty.includes(key)) continue;

      const useHyphen = useHyphenDelimiter.includes(key);
      event.detail[key] = adobeCleanString(value, useHyphen);
    }
  }

  // Allow these events to escape the shadowRoot by setting "composed: true"
  // To do this, we need to clone the incoming read only event so that the value
  // can be overridden. Doing it here prevents needing to add this flag
  // everywhere this method gets called.
  const customEvent = new CustomEvent(event.type, {
    detail: event.detail,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    composed: true,
  });

  if (!target) target = document.body;
  // If window.alloy is not yet available add the event to a queue that will
  // be scraped when alloy loads.
  if (window.alloy) {
    await target.dispatchEvent(customEvent);
  } else {
    window.awiEventQueue = window.awiEventQueue || [];
    window.awiEventQueue.push({
      event: customEvent,
      target: target,
    });
  }
};

export const stringIsUrl = string => {
  // This isn't a perfect test, but it's certainly good enough for any of our
  // urls that might get passed through.
  const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]{1,5})?(\/[^\s]*)?$/i;
  return urlRegex.test(string);
};

export const adobeCleanString = (str, allowHyphen = false) => {
  if (typeof str !== 'string') return str;

  // Replace the literal string "undefined" with an empty string
  str = str.replace(/undefined/g, '');

  // Replace line breaks with whitespace and remove HTML tags
  str = str.replace(/<br>/g, ' ');
  str = str.replace(/<[^>]*>/g, '');

  // Turn hyphens to white space if not allowed
  if (!allowHyphen) {
    str = str.replace(/-/g, ' ');
  }

  // Remove anything that is not alphanumeric, whitespace, pipe, or hyphen
  str = str.replace(/[^\w\s|\-]/g, '');

  // Collapse multiple spaces into one
  str = str.replace(/\s+/g, ' ');

  // Capitalize each word after the first pipe (`|`)
  str = str.replace(
    /\|(.+)/,
    (_, afterPipe) =>
      '|' +
      afterPipe.replace(
        /\b\w+/g,
        word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
  );

  return str.trim();
};

export const handleFileDownloadEvent = eventData => {
  if (!eventData) return;
  const downloadsAnalyticsDataObj =
    typeof eventData === 'string' ? JSON.parse(eventData) : eventData;

  // All fields are required, except search
  let {
    file_format,
    file_name,
    link_url,
    link_type,
    placement,
    search,
  } = downloadsAnalyticsDataObj;

  placement = adobeCleanString(placement).replace(/\|/g, ' ');
  file_name = adobeCleanString(file_name).replace(/\|/g, ' ');

  const formattedEventData = {
    type: 'download',
    detail: {
      eVar8: `FILE|${placement}|${file_name}`,
      eVar9: `${link_type}|${file_name}|${file_format}`,
      eVar109: link_url,
    },
  };

  if (typeof search === 'object') {
    formattedEventData.detail.search = search;
  }

  fireAdobeAnalyticsEvent(formattedEventData);
};

export const handleAdobeNavEvent = async navObj => {
  if (!navObj) return;
  navObj = isValidJsonString(navObj) ? JSON.parse(navObj) : navObj;
  const event = navObj.event || null;
  const target = event ? event.target : null;

  let analyticsCtaDataObject = navObj.analyticsCtaDataObject || '';
  let destinationUrl = navObj.destinationUrl || '';
  let navString = navObj.navString || '';

  if (isValidJsonString(analyticsCtaDataObject)) {
    analyticsCtaDataObject = JSON.parse(analyticsCtaDataObject);
    navString = navString || analyticsCtaDataObject.navString;
    destinationUrl = destinationUrl || analyticsCtaDataObject.destinationUrl;
  }

  const detailObj = {
    eVar8: navString,
  };

  if (navString.toLowerCase().includes('exit|')) {
    detailObj['eVar108'] = destinationUrl;
  }

  if (navString) {
    // navString == {NAV or INP}|{linkPlacement}|{linkName}
    const eventData = {
      type: 'click',
      detail: detailObj,
    };
    if (typeof navObj.search === 'object') {
      eventData['search'] = navObj.search;
    }
    await fireAdobeAnalyticsEvent(eventData, target);
  }

  if (!navString.startsWith('INP')) {
    destinationUrl =
      typeof destinationUrl === 'string'
        ? destinationUrl ||
          navObj.url ||
          navObj.cta?.url ||
          event?.currentTarget?.href ||
          ''
        : '';

    if (destinationUrl) {
      if (!!event) {
        event.preventDefault();
      }
      const useTargetBlank =
        navObj.target_blank ||
        navObj.target === '_blank' ||
        (destinationUrl.includes('http') &&
          !destinationUrl.includes('3-form.com'));
      setTimeout(() => {
        if (useTargetBlank) {
          window.open(destinationUrl, '_blank');
        } else {
          window.location.href = destinationUrl;
        }
      }, 150);
    }
  }
};

export const analyticsCtaDataObject = (dataObject = {}, placement = '') => {
  if (typeof dataObject === 'string') dataObject = JSON.parse(dataObject);
  let formattedAnalyticsObj = {};

  const placementStr = placement || dataObject.placement || '';
  const fileNameStr = dataObject.file_name || dataObject.text || '';
  const url = dataObject.url || dataObject.link_url;

  if (dataObject.file_format || dataObject.isDownload) {
    // File Download event formatting
    formattedAnalyticsObj = {
      file_format: dataObject.file_format,
      file_name: analyticsLabelFromString(fileNameStr),
      link_type: analyticsDownloadType(dataObject),
      link_url: url,
      isDownload: true,
      placement: analyticsLabelFromString(placementStr),
    };
  } else {
    const ctaNavName =
      dataObject.text || dataObject.cta?.text || dataObject.header;
    // Nav event formatting
    dataObject.isNav = true;
    dataObject.placement = placementStr;
    dataObject.navString = `NAV|${placement}|${ctaNavName}`;
    formattedAnalyticsObj = dataObject;
  }
  if (dataObject.search) formattedAnalyticsObj['search'] = dataObject.search;
  return JSON.stringify(formattedAnalyticsObj);
};

export const analyticsDownloadType = fileObj => {
  if (fileObj.link_type) {
    return fileObj.link_type;
  }
  // fileObj could be a few different object types. This is kind of messy, but for
  // now it's the best way to extract the download type from the different
  // data types that could be passed here.
  let downloadType = '';

  if (fileObj?.text?.toLowerCase().includes('revit')) {
    downloadType = 'REVIT';
  } else if (fileObj?.text?.toLowerCase().includes('cad')) {
    downloadType = 'CAD';
  } else if (fileObj?.file_format) {
    downloadType = fileObj.file_format;
  } else if (fileObj?.src && fileObj?.src.includes('image')) {
    downloadType = 'IMAGE';
  } else {
    downloadType = fileObj?.text || '';
  }

  return downloadType;
};

export const isValidJsonString = str => {
  if (typeof str !== 'string') return false;
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && typeof parsed === 'object';
  } catch {
    return false;
  }
};

export const analyticsLabelFromString = (str = '') => {
  // we need some way to extract meaningful data from image descriptions and
  // potentially long block titles
  // an example str would be:
  // Material | Varia<br>Interlayer | Rust Oxide<br>Hardware Finish | Carbide
  if (str.includes('<br>')) {
    if (str.includes('|')) {
      const matches = [...str.matchAll(/\|\s*(.*?)<br>?/g)].map(m => m[1]);
      return matches.join(' ');
    } else {
      return str.split('<br>')[0]; // Return everything before the first <br>
    }
  }
  // example output would be "Varia Rust Oxide Carbide"
  return truncatedAnalyticsString(str); // If neither <br> nor | exist, return the full string
};

export const truncatedAnalyticsString = (str, maxLength = 50) => {
  return str.slice(0, maxLength);
};

const setupGlobalSearchTracking = async globalSearchWrapper => {
  const wrapperData = JSON.parse(globalSearchWrapper.dataset.results);
  const resultsFound = !!wrapperData.results_found;
  const term = adobeCleanString(wrapperData.search_term);
  // setup search result click handler
  document
    .querySelector('#global-search-results')
    .addEventListener('click', function(event) {
      const target = event.target.closest('.js-search-result');
      if (target) {
        const resultData = JSON.parse(target.dataset.result);
        const linkTitle = resultData.link_title
          .replace('|', '')
          .replace('  ', ' ')
          .trim();
        const position = resultData.index + 1;
        const destinationUrl = target.getAttribute('href');

        handleAdobeNavEvent({
          navString: `NAV|Site Search|${linkTitle}`,
          destinationUrl,
          search: {
            term,
            position,
            results: resultsFound,
            toolName: 'Site Search',
          },
          event,
        });
      }
    });
};

const setupCEUsPageTracking = async ceusPageHeader => {
  const trackedLinks = document.querySelectorAll(
    'a.js-analytics-nav[data-analytics_string]'
  );
  const trackedINPs = document.querySelectorAll(
    'a.js-analytics-inp[data-analytics_string]'
  );

  trackedINPs.forEach(link => {
    link.addEventListener('click', () => {
      const label = link.getAttribute('data-analytics_string');
      const inpEventData = {
        type: 'click',
        detail: {
          eVar8: adobeCleanString(label),
        },
      };
      fireAdobeAnalyticsEvent(inpEventData);
    });
  });

  trackedLinks.forEach(link => {
    link.addEventListener('click', event => {
      const label = link.getAttribute('data-analytics_string');
      const destinationUrl = link.getAttribute('href');
      event.preventDefault();
      handleAdobeNavEvent({
        navString: label,
        destinationUrl,
        event,
      });
    });
  });
};

const setupBitreelDigitalPrintPageTracking = async bitreelDigitalPrintWrapper => {
  document.addEventListener(
    'bitreel-digital-print-request-quote-nav',
    event => {
      // incoming detail: { headerStr, imageStr, materialStr, orientationStr, opacityStr, destinationUrl, isCustomImage }
      const detail = event.detail || {};
      const { destinationUrl, isCustomImage } = detail;
      const customImageStr = isCustomImage ? 'User' : '3form';
      handleAdobeNavEvent({
        navString: `NAV|Digital Print Configurator ${customImageStr} Image|Request a Quote`,
        destinationUrl,
        event,
      });
    }
  );

  document.addEventListener(
    'bitreel-digital-print-download-configured-image',
    event => {
      // incoming detail: { format, background, resolution, isCustomImage }
      const detail = event.detail || {};
      const { format, background, resolution, isCustomImage } = detail;
      const customImageStr = isCustomImage ? 'User Provided' : '3form Library';
      const file_name = `Rendered Image ${customImageStr} ${background} ${resolution}`;
      const downloadDetails = {
        file_format: detail.format,
        file_name,
        link_url: 'dynamically created img',
        link_type: 'IMAGE',
        placement: 'Digital Print Configurator',
      };
      handleFileDownloadEvent(downloadDetails);
    }
  );

  document.addEventListener(
    'bitreel-digital-print-download-source-image',
    event => {
      // incoming detail: { filename, imageUrl, isCustomImage }
      const detail = event.detail || {};
      const { filename, imageUrl } = detail;
      const downloadDetails = {
        file_format: detail.format,
        file_name: filename,
        link_url: imageUrl,
        link_type: 'IMAGE',
        placement: 'Digital Print Configurator',
      };
      handleFileDownloadEvent(downloadDetails);
    }
  );
};
